// Vehicle session persistence — localStorage + custom events
// Supports up to 2 vehicles (slot 0 and slot 1)
// Consumed by: ai-intake.js (write), product-family-page.js (read), garage popover (read)

const STORAGE_KEY = 'formy_vehicles';
const LEGACY_KEY  = 'formy_vehicle';

const _SILHOUETTE_MAP = {
  'model 3':    'model-3.png',
  'model-3':    'model-3.png',
  'model3':     'model-3.png',
  'model s':    'model-s.png',
  'model-s':    'model-s.png',
  'models':     'model-s.png',
  'model x':    'model-x.png',
  'model-x':    'model-x.png',
  'modelx':     'model-x.png',
  'model y':    'model-y.png',
  'model-y':    'model-y.png',
  'modely':     'model-y.png',
  'cybertruck': 'model-cybertruck.png',
  'cyber truck':'model-cybertruck.png',
};

function _getAssetBase() {
  const header = document.querySelector('.site-header[data-asset-base]');
  return header ? header.dataset.assetBase : '';
}

function _getSilhouetteFilename(model) {
  if (!model) return null;
  return _SILHOUETTE_MAP[model.toLowerCase().trim()] || null;
}

const VehicleSession = window.VehicleSession = {

  // --- Read ---

  getAll() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      // Migrate legacy single-vehicle key
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) {
        const v = JSON.parse(legacy);
        this._save([v]);
        localStorage.removeItem(LEGACY_KEY);
        return [v];
      }
      return [];
    } catch { return []; }
  },

  get()      { return this.getAll()[0] || null; }, // backward compat
  getCount() { return this.getAll().length; },

  // --- Write ---

  set(vehicle) {
    // Backward-compat: fills first available slot, max 2
    const all = this.getAll();
    if (all.length < 2) all.push(vehicle);
    else all[0] = vehicle; // overwrite primary if both full
    this._save(all);
    document.dispatchEvent(new CustomEvent('vehicle:set', { detail: vehicle }));
    this._updateGarageUI();
  },

  setSlot(slot, vehicle) {
    const all = this.getAll();
    while (all.length <= slot) all.push(null);
    all[slot] = vehicle;
    this._save(all.filter(Boolean));
    document.dispatchEvent(new CustomEvent('vehicle:set', { detail: vehicle }));
    this._updateGarageUI();
  },

  clearSlot(slot) {
    const all = this.getAll();
    all.splice(slot, 1);
    this._save(all);
    document.dispatchEvent(new CustomEvent('vehicle:cleared', { detail: { slot } }));
    // Close popover if now empty
    if (all.length === 0) {
      const popover = document.getElementById('garage-popover');
      if (popover) popover.hidden = true;
    }
    this._updateGarageUI();
    // Quiet update — don't force-show widget or hide wizard/result
    this._renderHomepageGarage(this.getAll(), { quiet: true });
  },

  clear() {
    // Clears ALL vehicles and redirects home. Use clearSlot() for selective removal.
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
    document.dispatchEvent(new CustomEvent('vehicle:cleared'));
    this._updateGarageUI();
    window.location.href = '/';
  },

  _save(arr) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(0, 2)));
  },

  // --- UI Updates ---

  _updateGarageUI() {
    const all = this.getAll();
    const count = all.length;
    const assetBase = _getAssetBase();

    // Badge
    const badge = document.getElementById('garage-badge');
    if (badge) {
      badge.hidden = count === 0;
      badge.textContent = String(count);
    }

    // Header icon
    const headerIcon = document.getElementById('garage-header-icon');
    if (headerIcon && assetBase) {
      if (count === 0)      headerIcon.src = assetBase + 'garage-empty.svg';
      else if (count === 1) headerIcon.src = assetBase + 'garage-occupied-shell.svg';
      else                  headerIcon.src = assetBase + 'garage-full.svg';
    }

    this._renderPopover(all);
    // NOTE: _renderHomepageGarage is intentionally NOT called here.
    // It is only called from init() (page load) and clearSlot() (vehicle removed).
    // Calling it during set/setSlot would fight the active wizard flow.
  },

  _renderPopover(all) {
    const body = document.getElementById('garage-popover-body');
    if (!body) return;
    const assetBase = _getAssetBase();

    if (all.length === 0) {
      const emptyIcon = assetBase
        ? `<img src="${assetBase}garage-empty.svg" alt="" class="garage-popover__empty-icon" width="64" height="64" aria-hidden="true">`
        : '';
      body.innerHTML = `<div class="garage-popover__empty">${emptyIcon}No vehicle saved yet.<br><a href="/" class="garage-popover__add-link">Add your vehicle</a></div>`;
      return;
    }

    const vehicleHtml = all.map((vehicle, slot) => {
      const fitmentDisplay = (vehicle.fitment && vehicle.fitment !== 'standard')
        ? vehicle.fitment.replace(/-/g, ' ') : 'Standard';
      const trimDisplay = vehicle.trim
        ? vehicle.trim.charAt(0).toUpperCase() + vehicle.trim.slice(1)
        : '';
      const detailLine = trimDisplay ? `${trimDisplay} · ${fitmentDisplay}` : fitmentDisplay;
      const silhouetteFile = _getSilhouetteFilename(vehicle.model);
      const silhouetteHtml = (assetBase && silhouetteFile)
        ? `<div class="garage-popover__silhouette-wrap"><img src="${assetBase}${silhouetteFile}" alt="${vehicle.model}" class="garage-popover__silhouette${all.length > 1 ? ' garage-popover__silhouette--compact' : ''}" width="180"></div>`
        : '';
      return `
        <div class="garage-popover__vehicle${all.length > 1 ? ' garage-popover__vehicle--compact' : ''}">
          <button class="garage-popover__remove" type="button" onclick="VehicleSession.clearSlot(${slot})" aria-label="Remove vehicle"><svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l8 8M9 1l-8 8"/></svg></button>
          ${silhouetteHtml}
          <div class="garage-popover__name">${vehicle.year} Tesla ${vehicle.model.toUpperCase()}</div>
          <div class="garage-popover__fitment">${detailLine}</div>
          <div class="garage-popover__actions">
            <a href="/pages/families/${encodeURIComponent(vehicle.handle)}" class="garage-popover__action garage-popover__action--view">View products</a>
          </div>
        </div>`;
    }).join('<hr class="garage-popover__divider">');

    const addSlotHtml = all.length < 2
      ? `<div class="garage-popover__add-slot"><button class="garage-popover__add-vehicle-btn" type="button" id="garage-add-vehicle-btn"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>Add another vehicle</button></div>`
      : '';

    const disclaimerHtml = `<p class="garage-popover__disclaimer">Images are representative only — always verify fitment for your specific year and trim.</p>`;
    body.innerHTML = vehicleHtml + addSlotHtml + disclaimerHtml;

    // Bind "Add another vehicle" button in popover
    const addVehicleBtn = body.querySelector('#garage-add-vehicle-btn');
    if (addVehicleBtn) {
      addVehicleBtn.addEventListener('click', () => {
        // Close popover
        const popover = document.getElementById('garage-popover');
        if (popover) popover.hidden = true;

        // If on homepage, trigger wizard for slot 1
        const intake = document.querySelector('ai-intake-bar');
        const widget = document.getElementById('homepage-garage');
        const resultMount = document.getElementById('wizard-result-mount');
        if (intake) {
          if (widget) widget.hidden = true;
          if (resultMount) { resultMount.hidden = true; resultMount.innerHTML = ''; }
          document.documentElement.classList.remove('garage-has-vehicles');
          intake.hidden = false;
          if (typeof intake._reset === 'function') intake._reset(1);
        } else {
          // Not on homepage — navigate there and wizard will open
          window.location.href = '/?add-vehicle=1';
        }
      });
    }
  },

  _renderHomepageGarage(all, opts) {
    const quiet = opts && opts.quiet;
    const widget = document.getElementById('homepage-garage');
    if (!widget) return;

    const intake = document.querySelector('ai-intake-bar');
    const resultMount = document.getElementById('wizard-result-mount');

    if (all.length === 0) {
      widget.hidden = true;
      const disclaimer = document.getElementById('homepage-garage-disclaimer');
      if (disclaimer) disclaimer.hidden = true;
      // Remove flash-prevention class so wizard shows
      document.documentElement.classList.remove('garage-has-vehicles');
      // Restore homepage text elements hidden by _renderInlineResult
      ['.homepage-eyebrow', '.homepage-headline', '.homepage-sub'].forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) el.style.display = '';
      });
      if (intake) {
        intake.hidden = false;
        if (typeof intake._reset === 'function') intake._reset(0);
      }
      // Clear loaded family product when all vehicles removed
      if (resultMount) { resultMount.hidden = true; resultMount.innerHTML = ''; }
      return;
    }

    const assetBase = _getAssetBase();
    const carsEl = widget.querySelector('#homepage-garage-cars');
    if (!carsEl) return;

    const carCards = all.map((vehicle, slot) => {
      const silhouetteFile = _getSilhouetteFilename(vehicle.model);
      const imgHtml = (assetBase && silhouetteFile)
        ? `<img src="${assetBase}${silhouetteFile}" alt="${vehicle.model}" class="homepage-garage__silhouette" width="200">`
        : '';
      const fitmentDisplay = (vehicle.fitment && vehicle.fitment !== 'standard')
        ? vehicle.fitment.replace(/-/g, ' ') : 'Standard';
      const trimDisplay = vehicle.trim
        ? vehicle.trim.charAt(0).toUpperCase() + vehicle.trim.slice(1)
        : '';
      const detailLine = trimDisplay ? `${trimDisplay} · ${fitmentDisplay}` : fitmentDisplay;
      return `
        <div class="homepage-garage__car">
          <button class="homepage-garage__remove" type="button" onclick="VehicleSession.clearSlot(${slot})" aria-label="Remove vehicle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l8 8M9 1l-8 8"/></svg></button>
          ${imgHtml}
          <div class="homepage-garage__name">${vehicle.year} Tesla ${vehicle.model.toUpperCase()}</div>
          <div class="homepage-garage__fitment">${detailLine}</div>
          <div class="homepage-garage__actions">
            <a href="/pages/families/${encodeURIComponent(vehicle.handle)}" class="homepage-garage__btn homepage-garage__btn--primary">View products</a>
          </div>
        </div>`;
    }).join('');

    const addSlotHtml = all.length < 2 ? `
      <div class="homepage-garage__add-slot">
        <button class="homepage-garage__add-btn" type="button" id="homepage-add-vehicle">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          Add Another Vehicle to My Garage
        </button>
      </div>` : '';

    carsEl.innerHTML = carCards + addSlotHtml;

    // Bind "Add Another Vehicle"
    const addBtn = carsEl.querySelector('#homepage-add-vehicle');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        widget.hidden = true;
        const resultMount = document.getElementById('wizard-result-mount');
        if (resultMount) { resultMount.hidden = true; resultMount.innerHTML = ''; }
        document.documentElement.classList.remove('garage-has-vehicles');
        if (intake) {
          intake.hidden = false;
          if (typeof intake._reset === 'function') intake._reset(1);
        }
      });
    }

    const disclaimer = document.getElementById('homepage-garage-disclaimer');
    if (disclaimer) disclaimer.hidden = false;

    // Update garage title icon: 1 car → single bay, 2 cars → double bay
    const titleIcon = document.getElementById('homepage-garage-icon');
    if (titleIcon && assetBase) {
      titleIcon.src = all.length >= 2
        ? assetBase + 'garage-full.svg'
        : assetBase + 'garage-occupied-shell.svg';
    }

    if (!quiet) {
      widget.hidden = false;
      if (intake) intake.hidden = true;
      if (resultMount && !resultMount.hidden) resultMount.hidden = true;
    }
  },

  init() {
    this._updateGarageUI();

    // If redirected here to add a second vehicle (from non-homepage "Add another" click)
    const params = new URLSearchParams(window.location.search);
    if (params.get('add-vehicle') === '1') {
      // Clean up the URL param without a reload
      const cleanUrl = window.location.pathname + (window.location.hash || '');
      history.replaceState(null, '', cleanUrl);

      // Skip garage widget and open wizard directly for slot 1
      const intake = document.querySelector('ai-intake-bar');
      const widget = document.getElementById('homepage-garage');
      if (intake) {
        if (widget) widget.hidden = true;
        document.documentElement.classList.remove('garage-has-vehicles');
        intake.hidden = false;
        // _targetSlot and _reset will be called after intake initializes
        // Intake vehicles may not be loaded yet — wait for ready event
        document.addEventListener('ai-intake:ready', () => {
          if (typeof intake._reset === 'function') intake._reset(1);
        }, { once: true });
      }
      return;
    }

    this._renderHomepageGarage(this.getAll());
  },
};
